import dynamic from 'next/dynamic';
import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { useSearchDealsPageData } from '@/app/components/search/searchDealsPageProvider.tsx';
import { SearchModes } from '@/domainLogic/index.ts';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';
import { ViewMode } from '@/app/common/viewModeUtils.ts';
import { LAYOUT_TYPE } from '@/app/components/search/components/LayoutManager/utils.ts';
import ScrollContainer from '@/app/common/components/ScrollContainer';
import type { RouteType } from '../../../Curiosity';
import { getSearchParamsClient } from '../../../common/getSearchParams';
import getPills from './getPills';

import Pill from './Pill';
import { HorizontalFiltersContainer } from './styled';
import type { DynamicFiltersResponse } from '../../../utils/api/dynamicFiltersResponseTypes';
import ViewModeToggle from '../../SearchPageContainer/ListContainer/ViewModeToggle';
import useWindowDimensions from '../../../hooks/useWindowHandler';

const LoadLine = dynamic(() => import('@/app/components/Loadline.tsx'), { ssr: false });

const isElementVisibleInScrollableDiv = (element: HTMLDivElement, scrollableDiv: HTMLDivElement) => {
  const elementRect = element?.getBoundingClientRect();
  const divRect = scrollableDiv?.getBoundingClientRect();

  const hasElements = elementRect && divRect;
  return hasElements ? elementRect.left >= divRect.left && elementRect.right <= divRect.right + 2 : false;
};

export type HorizontalFiltersProps = {
  isIndex?: boolean;
  routeType: RouteType;
  isWaiting: boolean;
  isLoading?: boolean;
  dynamicFiltersResponse?: DynamicFiltersResponse;
  handleShowFilters: (val: boolean) => void;
};

const HorizontalFilters = memo(
  ({ isIndex, routeType, isWaiting, isLoading, dynamicFiltersResponse, handleShowFilters }: HorizontalFiltersProps) => {
    const { searchParams: searchParamsServer, searchInfoData, commonData, localization } = usePageWrapperData();
    const { searchMode } = searchInfoData;
    const { t } = useTranslation();
    const { productsData } = useSearchPageData();
    const { hasPreselectedFlight } = useSearchDealsPageData();
    const { listView, layoutType, dealsToCompare } = useContext(SearchPageContext);
    const isHO = searchMode === SearchModes.HO;
    const searchParams = getSearchParamsClient(searchParamsServer);
    const filtersContainerRef = useRef<HTMLDivElement>(null);
    const pillsContainerRef = useRef<HTMLDivElement>(null);
    const { isDesktop, isMobile, externalHeaderEnabled } = useCommonClient();
    const pills = getPills({
      commonData,
      localization,
      searchParams,
      searchInfoData,
      isDesktop,
      productsData,
      isWaiting,
      listView,
      routeType,
      handleShowFilters,
      hasPreselectedFlight,
      t,
    });
    const { width } = useWindowDimensions();
    const extendedFilters = isDesktop;
    const [showArrowLeft, setShowArrowLeft] = useState(false);
    const [showArrowRight, setShowArrowRight] = useState(false);

    useEffect(() => {
      handleOnScroll();
    }, [width, listView]);

    const handleOnScroll = () => {
      if (pillsContainerRef.current) {
        const { scrollLeft } = pillsContainerRef.current;

        if (scrollLeft > 0 && !showArrowLeft) {
          setShowArrowLeft(true);
        }
        if (scrollLeft === 0 && showArrowLeft) {
          setShowArrowLeft(false);
        }
        const pillsList = pills.filter((x) => x.filterName !== 'sort' && x.filterName !== 'all_filters');
        const element = pillsContainerRef.current.querySelectorAll(
          `#Pill-${pillsList[pillsList.length - 1].control}`
        )[0] as HTMLDivElement;
        const isIn = isElementVisibleInScrollableDiv(element, pillsContainerRef.current);

        if (isIn && showArrowRight) {
          setShowArrowRight(false);
        }
        if (!isIn && !showArrowRight) {
          setShowArrowRight(true);
        }
      }
    };

    if (filtersContainerRef.current && extendedFilters) {
      handleOnScroll();
    }

    const desktopPills = pills.filter((x) => x.filterName !== 'sort' && x.filterName !== 'all_filters');
    const viewModeButtonEnabled = !isIndex && isDesktop;
    const isComparisonToastShown = dealsToCompare?.length > 0;
    const isToast = layoutType === LAYOUT_TYPE.MOBILE_MAP || (layoutType === LAYOUT_TYPE.MOBILE_LIST && !isDesktop);

    return (
      <ErrorBoundary id="HorizontalFilters">
        <HorizontalFiltersContainer
          isIndex={isIndex}
          ref={filtersContainerRef}
          className="horizontal-filters-container"
          brand={localization.brand}
          isDesktop={isDesktop}
          isComparisonToastShown={isComparisonToastShown}
          isDark={isToast}
          isWaiting={isWaiting}
          isToast={isToast}
          externalHeaderEnabled={externalHeaderEnabled}
        >
          {viewModeButtonEnabled && (
            <ViewModeToggle viewMode={listView ? ViewMode.LIST : ViewMode.MAP} isWaiting={isWaiting} />
          )}
          {extendedFilters && (
            <ScrollContainer style={{ paddingRight: 2 }} styleInner={{ paddingLeft: 2, paddingRight: 2 }}>
              {desktopPills.map((pill) => (
                <Pill
                  key={pill.control || pill.iconLeft}
                  routeType={routeType}
                  isIndex={isIndex}
                  isWaiting={isWaiting}
                  dynamicFiltersResponse={dynamicFiltersResponse}
                  isToast={isToast}
                  isDark={isToast}
                  {...pill}
                />
              ))}
            </ScrollContainer>
          )}
          {pills
            .filter((x) => {
              if (extendedFilters) return x.filterName === 'sort' || x.filterName === 'all_filters';
              if (isMobile && !listView) return x.filterName !== 'sort' && x.filterName !== 'map_toggle';
              return true;
            })
            .map((pill) => (
              <Pill
                key={pill.control || pill.iconLeft}
                routeType={routeType}
                isIndex={isIndex}
                isWaiting={isWaiting}
                dynamicFiltersResponse={dynamicFiltersResponse}
                isToast={isToast}
                isDark={isToast}
                hideDivider={false}
                {...pill}
              />
            ))}
          {(isWaiting || isLoading) && isDesktop && isHO && <LoadLine />}
        </HorizontalFiltersContainer>
      </ErrorBoundary>
    );
  }
);

HorizontalFilters.displayName = 'HorizontalFilters';

export default HorizontalFilters;
